import { getDisplayedBookedSlot, bookedSlotDayMs, bookedSlotMinutes } from './bookedSlot.js';

export const sortEventsByStartData = (events) => {
    try {
        return events.sort((a, b) => new Date(a.start_date).getTime() > new Date(b.start_date).getTime() ? 1 : -1)
    } catch (e) {
        console.log("sort error! sortEventsByStartData");
        console.log(e);
        return events
    }
}


const compareTimeAscending = (timeA, timeB) => timeA.getTime() - timeB.getTime();
const compareTimeDescending = (timeA, timeB) => timeB.getTime() - timeA.getTime();

/**
 * Sorts events by start_hour (time) within the same date.
 * Assumes events are already sorted by start_date from the API.
 * Note: start_hour is stored as a full ISO datetime string, not just a time.
 *
 * @param {Array} events - Array of event objects (pre-sorted by date from API)
 * @param {'asc'|'desc'} order - Sort order: 'asc' (earlier times first) or 'desc' (later times first, default)
 * @returns {Array} Array with events on the same date sorted by time
 *
 * Sorting behavior:
 * - Events on different dates: maintains original API order
 * - Events on the same date WITH start_hour: sorted by time according to order param
 * - Events on the same date WITHOUT start_hour: placed before events with start_hour
 * - Example (desc): For same date, 18:30 appears before 17:30, which appears before 12:00
 * - Example (asc): For same date, 12:00 appears before 17:30, which appears before 18:30
 */
export const sortEventsByStartHour = (events, order = 'desc') => {
    try {
        if (!events?.length) {
            return [];
        }

        // Default to descending (later times first)
        let compareTime = compareTimeDescending;
        if (order === 'asc') {
            compareTime = compareTimeAscending;
        }

        // Preserve the API's day order via first-seen index, then sort by start_hour WITHIN
        // each day. The previous comparator returned 0 for events on different dates while
        // ordering same-date events by hour, which is not a valid strict-weak-ordering:
        // Array.prototype.sort then produced engine-/input-dependent results and the per-day
        // hour ordering was effectively dropped (the "hour ignored" bug). Decorating with the
        // original index makes this a stable, valid total order without assuming the input is
        // already grouped by date. Day key is normalized so "2026-06-10" and
        // "2026-06-10T00:00:00Z" group together.
        const dayKeyOf = (event) => {
            const d = new Date(event?.start_date);
            return isNaN(d.getTime()) ? String(event?.start_date ?? '') : d.toISOString().slice(0, 10);
        };

        const dayOrder = new Map();
        events.forEach((event) => {
            const key = dayKeyOf(event);
            if (!dayOrder.has(key)) dayOrder.set(key, dayOrder.size);
        });

        return events
            .map((event, index) => ({ event, index }))
            .sort((a, b) => {
                // Primary: keep the API's day order
                const dayA = dayOrder.get(dayKeyOf(a.event));
                const dayB = dayOrder.get(dayKeyOf(b.event));
                if (dayA !== dayB) return dayA - dayB;

                // Within the same day: events without an hour come first
                const aHasHour = !!a.event.start_hour;
                const bHasHour = !!b.event.start_hour;
                if (aHasHour !== bHasHour) return aHasHour ? 1 : -1;
                if (!aHasHour) return a.index - b.index;

                // Both have an hour: order by time, stable on ties
                const cmp = compareTime(new Date(a.event.start_hour), new Date(b.event.start_hour));
                return cmp !== 0 ? cmp : a.index - b.index;
            })
            .map(({ event }) => event);
    } catch (e) {
        console.log("sort error! sortEventsByStartHour");
        console.log(e);
        return events;
    }
}

export const isFutureEvent = (event) => {
    const now = new Date()
    now.setHours(0, 0, 0, 0);
    const eventStateDate = new Date(event.end_date ? event.end_date : event.start_date)
    eventStateDate.setHours(0, 0, 0, 0);
    return eventStateDate.getTime() >= now.getTime()
}


export const isPastEvent = (event) => {
    const now = new Date()
    now.setHours(0, 0, 0, 0);
    const eventStateDate = new Date(event.start_date)
    eventStateDate.setHours(0, 0, 0, 0);
    return eventStateDate.getTime() < now.getTime()
}

/**
 * Minutes since midnight for a card's start_hour (full ISO datetime string).
 * Returns null when there is no start_hour. Uses the local wall-clock hour/minute,
 * consistent with sortEventsByStartHour which sorts the same field via new Date().
 */
const startMinutesOfDay = (startHour) => {
    if (!startHour) return null;
    const d = new Date(startHour);
    if (isNaN(d.getTime())) return null;
    return d.getHours() * 60 + d.getMinutes();
};

/**
 * Sort a combined list of seller-page cards (slot virtual-events AND regular events)
 * into one chronological stream: by calendar date ascending, then by start-hour
 * time-of-day ascending (10:00 before 12:00). Cards without a start_hour sort before
 * timed cards on the same day. Ties preserve input order (stable).
 *
 * @param {Array} cards - Card objects with start_date and (optional) start_hour
 * @returns {Array} New, sorted array
 */
export const sortCardsChronologically = (cards) => {
    try {
        if (!cards?.length) return [];
        // Bucket by UTC calendar date so a date-only slot card ("YYYY-MM-DD") and an
        // ISO-datetime regular event on the same day land in the same bucket regardless of
        // the viewer's timezone. (new Date("YYYY-MM-DD") + setHours is local and shifts the
        // day west of UTC.) Matches sortEventsByStartHour's UTC day key above.
        const dayMs = (card) => {
            const raw = card?.start_date;
            if (!raw) return Number.POSITIVE_INFINITY;
            const m = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (m) return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            const d = new Date(raw);
            if (isNaN(d.getTime())) return Number.POSITIVE_INFINITY;
            return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
        };
        return cards
            .map((card, index) => ({ card, index }))
            .sort((a, b) => {
                const dayDiff = dayMs(a.card) - dayMs(b.card);
                // Guard NaN (both dates invalid → Infinity - Infinity) so we never hand
                // an undefined-sign value to .sort(); fall through to the stable tie-break.
                if (dayDiff !== 0 && !Number.isNaN(dayDiff)) return dayDiff;

                const minA = startMinutesOfDay(a.card.start_hour);
                const minB = startMinutesOfDay(b.card.start_hour);
                const aHas = minA !== null;
                const bHas = minB !== null;
                if (aHas !== bHas) return aHas ? 1 : -1; // no-hour first
                if (aHas && minA !== minB) return minA - minB;

                return a.index - b.index; // stable tie-break
            })
            .map(({ card }) => card);
    } catch (e) {
        console.log("sort error! sortCardsChronologically");
        console.log(e);
        return cards;
    }
};

/**
 * UTC calendar-day timestamp for an event's start_date, or null when unparseable.
 * Mirrors sortCardsChronologically's day bucketing so "YYYY-MM-DD" and full ISO
 * datetimes on the same day compare equal regardless of the viewer's timezone.
 */
const eventDayMs = (event) => {
    const raw = event?.start_date;
    if (!raw) return null;
    const m = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/**
 * The calendar day and time-of-day a booked-event card actually DISPLAYS.
 *
 * A time-slot card shows the booked slot, not `event.start_date`, so the sort has to key
 * off the same value or the visible dates come out unordered. Every booking of one slot
 * event shares a single `start_date`, so keying off the event tied for all of them and the
 * comparator fell through to input order — which is the guard's `createdAt` DESC pre-sort.
 * The result was a descending run of slot dates inside a list labelled "soonest first".
 *
 * Non-slot bookings return exactly the event-derived keys used before, so regular events
 * order identically.
 * @pure
 */
const displayedDayMs = (userEvent) => {
    const slot = getDisplayedBookedSlot(userEvent);
    if (slot) {
        const slotDay = bookedSlotDayMs(slot);
        if (slotDay !== null) return slotDay;
    }
    return eventDayMs(userEvent?.event);
};

/** @pure @see displayedDayMs */
const displayedMinutes = (userEvent) => {
    const slot = getDisplayedBookedSlot(userEvent);
    if (slot) {
        const slotMinutes = bookedSlotMinutes(slot);
        if (slotMinutes !== null) return slotMinutes;
    }
    return startMinutesOfDay(userEvent?.event?.start_hour);
};

/**
 * Sort booked-event records (each a userEvent with a nested `.event`) for the
 * end-user "My Account" list. Upcoming events come first, ordered soonest →
 * latest (by date, then time-of-day); past events follow, ordered most-recent
 * → oldest. Events with an unparseable date sink to the bottom of their block.
 * Stable on ties; returns a new array and never throws.
 *
 * Note on the date keys: the upcoming/past PARTITION uses isFutureEvent, which keys
 * off end_date (so an ongoing multi-day event with a past start but a future end is
 * treated as upcoming). The ordering WITHIN a block keys off whatever the card
 * DISPLAYS — see displayedDayMs: the booked slot for a time-slot booking, otherwise
 * start_date/start_hour. That is the invariant to preserve: order by the displayed
 * value so the visible dates stay monotonic. Do not switch the ordering key to
 * end_date, and do not key it off start_date for slot bookings — the card does not
 * show start_date for those, and both mistakes make the displayed dates look out of
 * order, which is the bug this sort fixes.
 *
 * Known gap: the partition still keys off the EVENT window, not the slot. A booking
 * whose slot has passed while the event's booking window is still open sorts into the
 * upcoming block. No such record existed in production data when this was written.
 *
 * @param {Array} userEvents - userEvent records, each with an `event` field
 * @returns {Array} New, sorted array
 */
export const sortUserEventsUpcomingFirst = (userEvents) => {
    try {
        if (!userEvents?.length) return [];
        return userEvents
            .map((userEvent, index) => ({ userEvent, index }))
            .sort((a, b) => {
                const eventA = a.userEvent?.event || {};
                const eventB = b.userEvent?.event || {};

                // Upcoming block before past block
                const aFuture = isFutureEvent(eventA);
                const bFuture = isFutureEvent(eventB);
                if (aFuture !== bFuture) return aFuture ? -1 : 1;

                // By calendar day: upcoming ascending (soonest first),
                // past descending (most recent first). Unknown dates sink last.
                const dayA = displayedDayMs(a.userEvent);
                const dayB = displayedDayMs(b.userEvent);
                if (dayA === null || dayB === null) {
                    if (dayA !== dayB) return dayA === null ? 1 : -1;
                } else if (dayA !== dayB) {
                    return aFuture ? dayA - dayB : dayB - dayA;
                }

                // Same day: by time-of-day, same direction as the date order.
                // No-hour events come first within the day.
                const minA = displayedMinutes(a.userEvent);
                const minB = displayedMinutes(b.userEvent);
                const aHas = minA !== null;
                const bHas = minB !== null;
                if (aHas !== bHas) return aHas ? 1 : -1;
                if (aHas && minA !== minB) return aFuture ? minA - minB : minB - minA;

                return a.index - b.index; // stable tie-break
            })
            .map(({ userEvent }) => userEvent);
    } catch (e) {
        console.log("sort error! sortUserEventsUpcomingFirst");
        console.log(e);
        return userEvents;
    }
};
